import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ds/Tip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  responsiveDialogBodyClass,
} from "@/components/ui/ResponsiveDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search,
  UserPlus,
  Mail,
  Phone,
  Loader2,
  ArrowLeft,
  Link2,
  Building,
  Building2,
  Unlink,
  KeyRound,
  Plus,
  Minus,
} from "@/components/ds/icons";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { cleanRut, formatRut, validateRut } from "@/lib/rut";
import { RutInput } from "@/components/RutInput";
import { PageHeader } from "@/components/ui/PageHeader";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

type EstadoRelacion = "pendiente" | "activa" | "rechazada";

interface Empresa {
  id: number;
  rut: string;
  nombre: string;
  telefono: string | null;
  estado: "pendiente" | "activa";
}

interface Contratista {
  relacion_id: number;
  estado_relacion: EstadoRelacion;
  empresa: Empresa;
  contacto_email: string | null;
  contratos_count: number | null;
}

const ESTADO_LABEL: Record<EstadoRelacion, string> = {
  pendiente: "Pendiente",
  activa: "Activa",
  rechazada: "Rechazada",
};

const ESTADO_VARIANT: Record<EstadoRelacion, "default" | "secondary" | "destructive" | "outline"> = {
  activa: "default",
  pendiente: "secondary",
  rechazada: "destructive",
};

export function Contratistas() {
  const [contratistas, setContratistas] = useState<Contratista[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [aDesvincular, setADesvincular] = useState<Contratista | null>(null);
  const [desvinculando, setDesvinculando] = useState(false);
  const [gestionando, setGestionando] = useState<Contratista | null>(null);

  const desvincular = async () => {
    if (!aDesvincular) return;
    setDesvinculando(true);
    try {
      await api.delete(`/comercial/contratistas/${aDesvincular.empresa.id}`);
      toast.success(`${aDesvincular.empresa.nombre} fue desvinculada.`);
      setADesvincular(null);
      cargar();
    } catch {
      toast.error("No se pudo desvincular la empresa.");
    } finally {
      setDesvinculando(false);
    }
  };

  const cargar = async () => {
    setIsLoading(true);
    try {
      const res = await api.get<{ data: Contratista[] }>("/comercial/contratistas");
      setContratistas(res.data);
    } catch {
      toast.error("No se pudo cargar el listado de contratistas.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contratistas;
    const rutQuery = cleanRut(q);
    return contratistas.filter(
      (c) =>
        c.empresa.nombre.toLowerCase().includes(q) ||
        (rutQuery !== "" && c.empresa.rut.toLowerCase().includes(rutQuery)) ||
        (c.contacto_email ?? "").toLowerCase().includes(q),
    );
  }, [contratistas, search]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Contratistas"
        subtitle="Empresas que trabajan para ti y a las que puedes ceder licencias."
        cta={{
          label: "Invitar Contratista",
          icon: UserPlus,
          onClick: () => setDialogOpen(true),
        }}
      />
      <InvitarContratistaDialog
        key={dialogOpen ? "open" : "closed"}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={() => {
          setDialogOpen(false);
          cargar();
        }}
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Empresas contratistas vinculadas</CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, RUT o email..."
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              {contratistas.length === 0
                ? "Aún no invitaste contratistas. Haz clic en 'Invitar Contratista' para empezar."
                : "No hay resultados para esa búsqueda."}
            </div>
          ) : (
            <div className="space-y-4">
              {filtered.map((c) => (
                <Card key={c.relacion_id}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                          <Building className="h-6 w-6 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <CardTitle className="text-lg truncate">{c.empresa.nombre}</CardTitle>
                          <CardDescription>{formatRut(c.empresa.rut)}</CardDescription>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <Badge variant={ESTADO_VARIANT[c.estado_relacion]}>
                          {ESTADO_LABEL[c.estado_relacion]}
                        </Badge>
                        <Badge variant="secondary">
                          {c.contratos_count ?? 0} contratos
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        {c.contacto_email && (
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span className="truncate">{c.contacto_email}</span>
                          </div>
                        )}
                        {c.empresa.telefono && (
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <span className="truncate">{c.empresa.telefono}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <Tip
                          texto={
                            c.estado_relacion !== "activa"
                              ? "La empresa debe aceptar la vinculación antes de cederle licencias"
                              : undefined
                          }
                        >
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setGestionando(c)}
                            disabled={c.estado_relacion !== "activa"}
                          >
                            <KeyRound className="h-4 w-4 mr-1" />
                            Licencias
                          </Button>
                        </Tip>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toast.info("Próximamente")}
                        >
                          Ver Contratos
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toast.info("Próximamente")}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setADesvincular(c)}
                        >
                          <Unlink className="h-4 w-4 mr-1" />
                          Desvincular
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ResponsiveDialog open={gestionando !== null} onOpenChange={(open) => !open && setGestionando(null)}>
        {gestionando && (
          <GestionarCuposDialog
            key={gestionando.empresa.id}
            contratista={gestionando}
            onClose={() => setGestionando(null)}
          />
        )}
      </ResponsiveDialog>

      <AlertDialog open={aDesvincular !== null} onOpenChange={(open) => !open && setADesvincular(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desvincular contratista</AlertDialogTitle>
            <AlertDialogDescription>
              {aDesvincular ? (
                <>
                  ¿Seguro que quieres desvincular a <strong>{aDesvincular.empresa.nombre}</strong>?
                  La empresa dejará de aparecer en tu listado.
                  {aDesvincular.contratos_count && aDesvincular.contratos_count > 0 ? (
                    <>
                      {" "}
                      Los <strong>{aDesvincular.contratos_count} contratos</strong> existentes
                      quedarán archivados como históricos.
                    </>
                  ) : null}{" "}
                  Si después la quieres volver a vincular, puedes hacerlo desde "Invitar
                  Contratista".
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={desvinculando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                desvincular();
              }}
              disabled={desvinculando}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {desvinculando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Desvincular"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface Candidato {
  empresa_id: number;
  empresa_nombre: string;
  contacto_nombre: string;
}

interface BuscarResponse {
  tipo: "nuevo" | "existente" | "duplicado" | "auto" | "multiple";
  message?: string;
  empresa?: { id: number; nombre: string };
  contacto?: { nombre: string; email: string };
  candidatos?: Candidato[];
}

/**
 * `store`/`vincular` pueden devolver 422 con `{ candidatos: [...] }` (mismo
 * shape que `buscar` tipo 'multiple') cuando aparecen N owners entre la
 * verificación inicial y el envío. Se extrae de forma segura desde el body
 * crudo de la respuesta de error (`ApiError.data`).
 */
function extraerCandidatos(data: unknown): Candidato[] | undefined {
  if (!data || typeof data !== "object") return undefined;
  const candidatos = (data as { candidatos?: unknown }).candidatos;
  return Array.isArray(candidatos) ? (candidatos as Candidato[]) : undefined;
}

type Step = "email" | "nuevo" | "existente" | "multiple";

interface InvitarContratistaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

function InvitarContratistaDialog({ open, onOpenChange, onCreated }: InvitarContratistaDialogProps) {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [empresaExistente, setEmpresaExistente] = useState<{ nombre: string } | null>(null);
  const [contactoExistente, setContactoExistente] = useState<{ nombre: string } | null>(null);
  const [candidatos, setCandidatos] = useState<Candidato[]>([]);
  const [empresaIdSeleccionada, setEmpresaIdSeleccionada] = useState<number | null>(null);
  const [empresaRut, setEmpresaRut] = useState("");
  const [empresaNombre, setEmpresaNombre] = useState("");
  const [contratistaNombre, setContratistaNombre] = useState("");
  const [contratistaApellido, setContratistaApellido] = useState("");
  const [isVerificando, setIsVerificando] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRutError, setShowRutError] = useState(false);

  const verificar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error("Ingresa un correo.");
      return;
    }

    setIsVerificando(true);
    try {
      const res = await api.get<BuscarResponse>(
        `/comercial/contratistas/buscar?email=${encodeURIComponent(email)}`,
      );

      if (res.tipo === "nuevo") {
        setStep("nuevo");
      } else if (res.tipo === "existente" && res.empresa) {
        setEmpresaExistente(res.empresa);
        setContactoExistente(res.contacto ?? null);
        setStep("existente");
      } else if (res.tipo === "multiple" && res.candidatos && res.candidatos.length > 0) {
        setCandidatos(res.candidatos);
        setEmpresaIdSeleccionada(null);
        setStep("multiple");
      } else if (res.tipo === "duplicado" || res.tipo === "auto") {
        toast.error(res.message ?? "No es posible enviar la invitación.");
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.errors) {
        const firstError = Object.values(err.errors)[0]?.[0];
        toast.error(firstError ?? "Correo inválido.");
      } else {
        toast.error("No se pudo verificar el correo.");
      }
    } finally {
      setIsVerificando(false);
    }
  };

  const submitNuevo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!empresaRut || !empresaNombre || !contratistaNombre || !contratistaApellido) {
      toast.error("Completa todos los campos.");
      return;
    }

    if (!validateRut(empresaRut)) {
      setShowRutError(true);
      toast.error("El RUT de la empresa es inválido.");
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post("/comercial/contratistas", {
        empresa_rut: empresaRut,
        empresa_nombre: empresaNombre,
        contratista_nombre: contratistaNombre,
        contratista_apellido: contratistaApellido,
        contratista_email: email,
      });

      toast.success(`Se envió email de registro a ${email}.`);
      onCreated();
    } catch (err) {
      handleSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitVincular = async (empresaId?: number) => {
    setIsSubmitting(true);
    try {
      await api.post("/comercial/contratistas/vincular", {
        contratista_email: email,
        ...(empresaId ? { empresa_id: empresaId } : {}),
      });
      toast.success(`Se envió email de vinculación a ${email}.`);
      onCreated();
    } catch (err) {
      handleSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitError = (err: unknown) => {
    if (err instanceof ApiError && err.status === 422) {
      const candidatosDesambiguar = extraerCandidatos(err.data);
      if (candidatosDesambiguar && candidatosDesambiguar.length > 0) {
        setCandidatos(candidatosDesambiguar);
        setEmpresaIdSeleccionada(null);
        setStep("multiple");
        toast.error("Hay más de una cuenta con ese correo. Elige la empresa correcta.");
        return;
      }
      if (err.errors) {
        const firstError = Object.values(err.errors)[0]?.[0];
        toast.error(firstError ?? "Revisa los datos.");
        return;
      }
    }
    toast.error("No se pudo enviar la invitación.");
  };

  const volver = () => {
    setStep("email");
    setEmpresaExistente(null);
    setContactoExistente(null);
    setCandidatos([]);
    setEmpresaIdSeleccionada(null);
    setEmpresaRut("");
    setEmpresaNombre("");
    setContratistaNombre("");
    setContratistaApellido("");
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      dismissible={step === "email"}
    >
      <ResponsiveDialogContent className="sm:max-w-md">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>Invitar contratista</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {step === "email"
              ? "Ingresa el correo del contacto. Si ya tiene cuenta, te lo vinculamos directo."
              : step === "existente"
                ? "Este correo ya tiene una cuenta. Vamos a vincular tu empresa con la suya como contratista."
                : step === "multiple"
                  ? "Hay más de una cuenta registrada con ese correo. Elige la empresa correcta."
                  : "Carga los datos de la empresa contratista. Le vamos a enviar un email para que confirme su registro."}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        {step === "email" && (
          <form onSubmit={verificar} className={responsiveDialogBodyClass}>
            <div className="space-y-2">
              <Label htmlFor="email">Email del contacto</Label>
              <Input
                id="email"
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="contacto@empresa.cl"
                disabled={isVerificando}
              />
            </div>
            <ResponsiveDialogFooter>
              <Button type="submit" disabled={isVerificando} className="gap-2">
                {isVerificando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Verificar
              </Button>
            </ResponsiveDialogFooter>
          </form>
        )}

        {step === "existente" && empresaExistente && (
          <div className={responsiveDialogBodyClass}>
            <div className="rounded-lg border bg-muted/40 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <div className="min-w-0">
                  <div className="font-medium truncate">{empresaExistente.nombre}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="truncate">{email}</span>
                {contactoExistente && (
                  <span className="text-muted-foreground">— {contactoExistente.nombre}</span>
                )}
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Le vamos a enviar un email para que acepte la vinculación. Hasta que la acepte, la
              relación queda en estado pendiente.
            </p>
            <ResponsiveDialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={volver} disabled={isSubmitting}>
                <ArrowLeft className="h-4 w-4" />
                Atrás
              </Button>
              <Button
                type="button"
                onClick={() => submitVincular()}
                disabled={isSubmitting}
                className="gap-2"
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                Vincular
              </Button>
            </ResponsiveDialogFooter>
          </div>
        )}

        {step === "multiple" && (
          <div className={responsiveDialogBodyClass}>
            <p className="text-sm text-muted-foreground">
              Encontramos {candidatos.length} empresas registradas con el correo{" "}
              <strong>{email}</strong>. Elige la empresa correcta.
            </p>
            <RadioGroup
              value={empresaIdSeleccionada !== null ? String(empresaIdSeleccionada) : ""}
              onValueChange={(value) => setEmpresaIdSeleccionada(Number(value))}
            >
              {candidatos.map((c) => (
                <Label
                  key={c.empresa_id}
                  htmlFor={`contratista-candidato-${c.empresa_id}`}
                  className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-muted/40"
                >
                  <RadioGroupItem
                    value={String(c.empresa_id)}
                    id={`contratista-candidato-${c.empresa_id}`}
                  />
                  <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.empresa_nombre}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.contacto_nombre}</div>
                  </div>
                </Label>
              ))}
            </RadioGroup>
            <ResponsiveDialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={volver} disabled={isSubmitting}>
                <ArrowLeft className="h-4 w-4" />
                Atrás
              </Button>
              <Button
                type="button"
                onClick={() => submitVincular(empresaIdSeleccionada ?? undefined)}
                disabled={isSubmitting || empresaIdSeleccionada === null}
                className="gap-2"
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                Vincular
              </Button>
            </ResponsiveDialogFooter>
          </div>
        )}

        {step === "nuevo" && (
          <form onSubmit={submitNuevo} className={responsiveDialogBodyClass}>
            <div className="space-y-2">
              <Label>Email del contacto</Label>
              <Input value={email} disabled />
            </div>
            <div className="space-y-2">
              <Label htmlFor="empresa-rut">RUT empresa</Label>
              <RutInput
                id="empresa-rut"
                value={empresaRut}
                onChange={setEmpresaRut}
                placeholder="76.123.456-7"
                disabled={isSubmitting}
                validate
                showError={showRutError}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="empresa-nombre">Nombre empresa</Label>
              <Input
                id="empresa-nombre"
                value={empresaNombre}
                onChange={(e) => setEmpresaNombre(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="contacto-nombre">Nombre contacto</Label>
                <Input
                  id="contacto-nombre"
                  value={contratistaNombre}
                  onChange={(e) => setContratistaNombre(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contacto-apellido">Apellido</Label>
                <Input
                  id="contacto-apellido"
                  value={contratistaApellido}
                  onChange={(e) => setContratistaApellido(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <ResponsiveDialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={volver} disabled={isSubmitting}>
                <ArrowLeft className="h-4 w-4" />
                Atrás
              </Button>
              <Button type="submit" disabled={isSubmitting} className="gap-2">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                Enviar invitación
              </Button>
            </ResponsiveDialogFooter>
          </form>
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}

interface CesionResumen {
  total: number;
  usadas: number;
  disponibles: number;
}

interface GestionarCuposDialogProps {
  contratista: Contratista;
  onClose: () => void;
}

function GestionarCuposDialog({ contratista, onClose }: GestionarCuposDialogProps) {
  const [resumen, setResumen] = useState<CesionResumen | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accion, setAccion] = useState<"ceder" | "revocar">("ceder");
  const [cantidad, setCantidad] = useState("1");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const empresaId = contratista.empresa.id;

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    api
      .get<{ data: CesionResumen }>(`/comercial/contratistas/${empresaId}/cesiones`)
      .then((res) => {
        if (!cancelled) setResumen(res.data);
      })
      .catch(() => {
        if (!cancelled) toast.error("No se pudo cargar el resumen de licencias cedidas.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [empresaId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const n = Number(cantidad);
    if (!Number.isInteger(n) || n < 1) {
      toast.error("Ingresa una cantidad válida.");
      return;
    }

    setIsSubmitting(true);
    try {
      const path = `/comercial/contratistas/${empresaId}/cesiones`;
      const res =
        accion === "ceder"
          ? await api.post<{ data: CesionResumen }>(path, { cantidad: n })
          : await api.delete<{ data: CesionResumen }>(path, { cantidad: n });

      setResumen(res.data);
      setCantidad("1");
      toast.success(
        accion === "ceder"
          ? `Cediste ${n} licencia(s) a ${contratista.empresa.nombre}.`
          : `Revocaste ${n} licencia(s) a ${contratista.empresa.nombre}.`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.errors) {
        const firstError = Object.values(err.errors)[0]?.[0];
        toast.error(firstError ?? err.message ?? "No se pudo completar la operación.");
      } else if (err instanceof ApiError) {
        toast.error(err.message ?? "No se pudo completar la operación.");
      } else {
        toast.error("No se pudo completar la operación.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ResponsiveDialogContent className="sm:max-w-md">
      <ResponsiveDialogHeader>
        <ResponsiveDialogTitle>Licencias cedidas a {contratista.empresa.nombre}</ResponsiveDialogTitle>
        <ResponsiveDialogDescription>
          Cede licencias de tu suscripción para que el contratista las asigne a sus trabajadores.
        </ResponsiveDialogDescription>
      </ResponsiveDialogHeader>

      {isLoading || !resumen ? (
        <div className="flex flex-1 items-center justify-center p-4 py-8 md:p-[18px]">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <form onSubmit={submit} className={responsiveDialogBodyClass}>
          <div className="grid grid-cols-3 gap-2 rounded-lg border p-3 text-center">
            <div>
              <div className="text-xs text-muted-foreground">Cedidas</div>
              <div className="text-2xl font-bold">{resumen.total}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">En uso</div>
              <div className="text-2xl font-bold">{resumen.usadas}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Libres</div>
              <div className="text-2xl font-bold text-success">{resumen.disponibles}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant={accion === "ceder" ? "default" : "outline"}
              onClick={() => setAccion("ceder")}
              disabled={isSubmitting}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Ceder
            </Button>
            <Tip
              texto={
                resumen.disponibles === 0
                  ? "No hay licencias libres para revocar"
                  : undefined
              }
            >
              <Button
                type="button"
                variant={accion === "revocar" ? "default" : "outline"}
                onClick={() => setAccion("revocar")}
                disabled={isSubmitting || resumen.disponibles === 0}
                className="gap-2"
              >
                <Minus className="h-4 w-4" />
                Revocar
              </Button>
            </Tip>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cantidad">Cantidad</Label>
            <Input
              id="cantidad"
              type="number"
              min={1}
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
              disabled={isSubmitting}
              autoFocus
            />
            {accion === "revocar" && (
              <p className="text-xs text-muted-foreground">
                Solo se revocan licencias libres. Las que ya están en uso por trabajadores del
                contratista quedan vigentes hasta que él las libere.
              </p>
            )}
          </div>

          <ResponsiveDialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cerrar
            </Button>
            <Button type="submit" disabled={isSubmitting} className="gap-2">
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Confirmar
            </Button>
          </ResponsiveDialogFooter>
        </form>
      )}
    </ResponsiveDialogContent>
  );
}
